import React, { useState } from 'react';
import PropTypes from 'prop-types';
import IfClauseEditor from './IfClauseEditor';
import BranchFieldsEditor from './BranchFieldsEditor';
import RuleValidationSummary from './RuleValidationSummary';
import { summarize } from './summarize';

/**
 * One rule's editor. Composes IfClauseEditor + THEN / ELSE
 * BranchFieldsEditor. Owns local collapsed state. ELSE branch is hidden
 * until the user clicks "+ Add ELSE branch".
 */
export default function RuleCard({
	rule,
	ruleIndex,
	availableFields,
	errors,
	onChange,
	onDelete,
	onDuplicate,
}) {
	const [collapsed, setCollapsed] = useState(false);
	// ELSE branch is "open" whenever the rule already has elseFields OR the
	// user explicitly clicked `+ Add ELSE branch`. Tracked locally so an empty
	// elseFields array still renders the editor.
	const [elseOpen, setElseOpen] = useState(rule.elseFields.length > 0);

	const patch = (partial) => onChange({ ...rule, ...partial });

	const hasErrors = !!errors && Object.keys(errors).length > 0;
	const showElseEditor = elseOpen || rule.elseFields.length > 0;

	return (
		<div
			className={`dsv-xallof-builder__rule${hasErrors ? ' dsv-xallof-builder__rule--invalid' : ''}${collapsed ? ' dsv-xallof-builder__rule--collapsed' : ''}`}
			role="group"
			aria-label={`Conditional rule ${ruleIndex + 1}`}
			data-testid={`xallof-rule-${ruleIndex}`}
		>
			<div className="dsv-xallof-builder__rule-header">
				<button
					type="button"
					className="dsv-xallof-builder__collapse-toggle"
					onClick={() => setCollapsed(!collapsed)}
					aria-expanded={!collapsed}
					data-testid={`xallof-rule-${ruleIndex}-collapse-toggle`}
				>
					<span className="dsv-xallof-builder__chevron" aria-hidden="true">
						{collapsed ? '▸' : '▾'}
					</span>
					<span className="dsv-xallof-builder__rule-index">
						Rule {ruleIndex + 1}
					</span>
					{rule.negated && (
						<span className="dsv-xallof-builder__neg-badge">NOT</span>
					)}
					{hasErrors && (
						<span
							className="dsv-xallof-builder__error-dot"
							aria-label="Has validation errors"
							title="Has validation errors"
						/>
					)}
					{collapsed && (
						<span className="dsv-xallof-builder__rule-summary">
							{summarize(rule)}
						</span>
					)}
				</button>
				<div className="dsv-xallof-builder__rule-actions">
					{onDuplicate && (
						<button
							type="button"
							className="dsv-xallof-builder__ghost-btn"
							onClick={onDuplicate}
							title="Duplicate rule"
							data-testid={`xallof-rule-${ruleIndex}-duplicate`}
						>
							Duplicate
						</button>
					)}
					<button
						type="button"
						className="dsv-xallof-builder__ghost-btn dsv-xallof-builder__ghost-btn--danger"
						onClick={onDelete}
						title="Delete rule"
						data-testid={`xallof-rule-${ruleIndex}-delete`}
					>
						Delete
					</button>
				</div>
			</div>

			{!collapsed && (
				<div className="dsv-xallof-builder__rule-body">
					<IfClauseEditor
						ifConditions={rule.ifConditions}
						negated={rule.negated}
						availableFields={availableFields}
						ruleIndex={ruleIndex}
						errors={errors?.ifConditions}
						onChange={(next) => patch({ ifConditions: next })}
						onNegatedChange={(n) => patch({ negated: n })}
					/>

					<section
						className="dsv-xallof-builder__section dsv-xallof-builder__section--then"
						aria-label="THEN branch"
					>
						<header className="dsv-xallof-builder__section-head">
							<span className="dsv-xallof-builder__section-label dsv-xallof-builder__section-label--then">
								THEN
							</span>
							<span className="dsv-xallof-builder__section-meta">
								apply these constraints when the IF matches
							</span>
						</header>
						<BranchFieldsEditor
							branch="then"
							ruleIndex={ruleIndex}
							selectedFields={rule.thenFields}
							availableFields={availableFields}
							errors={errors?.thenFields}
							onChange={(next) => patch({ thenFields: next })}
						/>
					</section>

					<section
						className="dsv-xallof-builder__section dsv-xallof-builder__section--else"
						aria-label="ELSE branch"
					>
						<header className="dsv-xallof-builder__section-head">
							<span className="dsv-xallof-builder__section-label dsv-xallof-builder__section-label--else">
								ELSE
							</span>
							<span className="dsv-xallof-builder__section-meta">
								{showElseEditor
									? 'constraints applied when the IF does not match'
									: 'optional — constraints when the IF does not match'}
							</span>
							{showElseEditor && (
								<button
									type="button"
									className="dsv-xallof-builder__ghost-btn dsv-xallof-builder__ghost-btn--sm"
									onClick={() => {
										setElseOpen(false);
										patch({ elseFields: [] });
									}}
									title="Remove ELSE branch"
								>
									Remove branch
								</button>
							)}
						</header>
						{showElseEditor ? (
							<BranchFieldsEditor
								branch="else"
								ruleIndex={ruleIndex}
								selectedFields={rule.elseFields}
								availableFields={availableFields}
								errors={errors?.elseFields}
								onChange={(next) => patch({ elseFields: next })}
							/>
						) : (
							<button
								type="button"
								className="dsv-xallof-builder__add-cond"
								onClick={() => setElseOpen(true)}
								data-testid={`xallof-rule-${ruleIndex}-add-else-branch`}
							>
								+ Add ELSE branch
							</button>
						)}
					</section>

					<RuleValidationSummary errors={errors} ruleIndex={ruleIndex} />
				</div>
			)}
		</div>
	);
}

RuleCard.propTypes = {
	rule: PropTypes.object.isRequired,
	ruleIndex: PropTypes.number.isRequired,
	availableFields: PropTypes.array.isRequired,
	errors: PropTypes.object,
	onChange: PropTypes.func.isRequired,
	onDelete: PropTypes.func.isRequired,
	onDuplicate: PropTypes.func,
};
